export type ResetViewButtonPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface ResetViewButtonOptions {
  /** Texte de l'infobulle et libellé d'accessibilité. Par défaut : "Recentrer la carte". */
  label?: string;
  /** Coin de l'écran où placer le bouton. Par défaut : "bottom-right", au-dessus de `ZoomControl` et
   * `GeolocationButton` (voir `stackOffsetPx`). */
  position?: ResetViewButtonPosition;
  /**
   * Fusionné par-dessus le style par défaut (position, apparence) — permet par ex. de masquer le
   * bouton (`{ display: "none" }`), d'ajuster ses marges, ou de changer son apparence, sans avoir à
   * connaître son CSS interne. Contrairement à `ContextMenu`/`FpsMeter` (entièrement stylés par le
   * CSS du consommateur, voir CLAUDE.md), ce bouton applique lui-même un style inline par défaut :
   * sa position est un réglage de configuration explicite (`position` ci-dessus), pas juste une
   * question d'apparence laissée au consommateur.
   */
  style?: Partial<CSSStyleDeclaration>;
}

const POSITION_OFFSETS: Record<ResetViewButtonPosition, Partial<CSSStyleDeclaration>> = {
  "top-left": { top: "10px", left: "10px" },
  "top-right": { top: "10px", right: "10px" },
  "bottom-left": { bottom: "10px", left: "10px" },
  "bottom-right": { bottom: "10px", right: "10px" },
};

const DEFAULT_STYLE: Partial<CSSStyleDeclaration> = {
  // Relatif au `container` passé à `createTokaMap` (rendu positionné pour l'occasion si besoin,
  // voir `createTokaMap.ts`), pas à la page entière — ex. ne chevauche pas une barre d'outils
  // au-dessus de `container` (`TopBar`, côté exemple).
  position: "absolute",
  zIndex: "10",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "36px",
  padding: "0",
  background: "#fff",
  color: "#444",
  border: "1px solid #ddd",
  // Carré arrondi, pas un cercle : s'empile avec le bloc à deux boutons de `ZoomControl`, qui ne
  // peut pas être rond — une colonne mêlant les deux formes ne se lit pas comme un ensemble.
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  cursor: "pointer",
};

/**
 * Bouton "recentrer la carte sur sa position d'origine" — appartient à `@tokamap/core` (pas un
 * widget que chaque consommateur doit reconstruire, contrairement à `Toolbar`/`Actions`/`UndoRedo`,
 * volontairement laissés à l'exemple, voir CLAUDE.md) car son comportement (revenir à la vue
 * chargée au démarrage) ne dépend d'aucune logique métier propre au consommateur.
 *
 * Masqué tant que la vue courante correspond encore à la vue de départ (voir `setVisible`, piloté
 * par `createTokaMap` via `"map-view:changed"`) : inutile de proposer de "recentrer" une carte qui
 * n'a pas bougé.
 */
export class ResetViewButton {
  private el: HTMLButtonElement;
  /** Valeur de `display` à utiliser quand le bouton est visible (celle issue de `DEFAULT_STYLE`,
   * éventuellement remplacée par `options.style.display`) — capturée avant le premier
   * `setVisible(false)` pour que `setVisible(true)` puisse la restaurer sans deviner un défaut. */
  private visibleDisplay: string;
  /** Bord sur lequel `setStackOffset` agit : un coin `"bottom-*"` se décale depuis le bas. */
  private edgeProperty: "top" | "bottom";

  constructor(container: HTMLElement, options: ResetViewButtonOptions, onReset: () => void) {
    this.el = document.createElement("button");
    this.el.type = "button";
    this.el.className = "toka-reset-view-button";
    this.el.appendChild(createResetViewIcon());
    const label = options.label ?? "Recentrer la carte";
    this.el.setAttribute("aria-label", label);
    // `title` en plus d'`aria-label` : sans lui, aucune infobulle au survol — l'icône seule ne dit
    // pas ce que fait le bouton à qui le découvre.
    this.el.title = label;
    Object.assign(this.el.style, DEFAULT_STYLE, POSITION_OFFSETS[options.position ?? "bottom-right"], options.style);
    this.visibleDisplay = this.el.style.display;
    this.edgeProperty = (options.position ?? "bottom-right").startsWith("top") ? "top" : "bottom";
    this.el.addEventListener("click", onReset);
    container.appendChild(this.el);
    // Masqué au départ : la vue de départ est par définition la vue courante tant que rien n'a
    // encore changé (voir `createTokaMap`, qui appelle `setVisible(true)` dès le premier
    // `"map-view:changed"` qui diverge de `initialView`).
    this.setVisible(false);
  }

  setVisible(visible: boolean): void {
    this.el.style.display = visible ? this.visibleDisplay : "none";
  }

  /** Voir `GeolocationButton.setStackOffset`. */
  setStackOffset(offsetPx: number): void {
    this.el.style[this.edgeProperty] = `${offsetPx}px`;
  }

  destroy(): void {
    this.el.remove();
  }
}

/** Icône "cadre" (quatre coins et un point central) : évoque le cadre de la vue de départ, et se
 * distingue de la cible de `GeolocationButton` comme de la flèche d'annulation de l'éditeur. */
function createResetViewIcon(): SVGSVGElement {
  const SVG_NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(SVG_NS, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "18");
  svg.setAttribute("height", "18");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const corners = ["M3 8V5a2 2 0 0 1 2-2h3", "M16 3h3a2 2 0 0 1 2 2v3", "M21 16v3a2 2 0 0 1-2 2h-3", "M8 21H5a2 2 0 0 1-2-2v-3"];
  for (const d of corners) {
    const corner = document.createElementNS(SVG_NS, "path");
    corner.setAttribute("d", d);
    svg.appendChild(corner);
  }
  const center = document.createElementNS(SVG_NS, "circle");
  center.setAttribute("cx", "12");
  center.setAttribute("cy", "12");
  center.setAttribute("r", "1.5");
  center.setAttribute("fill", "currentColor");
  svg.appendChild(center);
  return svg;
}
